/**
 * Admin Pages — inventory of public / marketing URLs operators can jump to,
 * joined with first-party visit + conversion counts.
 *
 * Not a CMS. Static catalog + runtime rows (campaign landings, hubs, projects,
 * SEO pages) assembled on the server.
 *
 * Unlisted = live at a public URL but intentionally absent from primary nav
 * and the XML sitemap (energy-briefing / overview pattern: noindex, direct URL).
 */

export type SitePageVisibility = "listed" | "unlisted";

export type SitePageKind =
  | "static"
  | "campaign"
  | "division"
  | "project"
  | "link_hub"
  | "seo"
  | "other";

export const PAGE_PERFORMANCE_PERIODS = ["1", "7", "30", "90", "all"] as const;
export type PagePerformancePeriod = (typeof PAGE_PERFORMANCE_PERIODS)[number];

const MS_PER_DAY = 24 * 60 * 60 * 1000;

export function isPagePerformancePeriod(
  value: string,
): value is PagePerformancePeriod {
  return (PAGE_PERFORMANCE_PERIODS as readonly string[]).includes(value);
}

/**
 * Start of the performance window, or null for "all".
 *
 * 7d / 30d / 90d stay the inclusive start of the UTC calendar day `period`
 * days ago. 1d is a rolling 24 hours before `now` on that same UTC clock —
 * not midnight today, which would be an open day-bucket and read as empty
 * until the UTC day closes.
 *
 * Visits are stored as UTC dates; signups and inquiries use this same instant.
 */
export function pagePerformancePeriodStartUtc(
  period: PagePerformancePeriod,
  now: Date = new Date(),
): Date | null {
  if (period === "all") return null;
  if (period === "1") return new Date(now.getTime() - MS_PER_DAY);
  const start = new Date(now);
  start.setUTCDate(start.getUTCDate() - Number(period));
  start.setUTCHours(0, 0, 0, 0);
  return start;
}

/** True when `instant` is exactly 00:00:00.000 UTC. */
export function isUtcDayStart(instant: Date): boolean {
  return (
    instant.getUTCHours() === 0 &&
    instant.getUTCMinutes() === 0 &&
    instant.getUTCSeconds() === 0 &&
    instant.getUTCMilliseconds() === 0
  );
}

/**
 * 7d/30d/90d cutoffs land on a UTC midnight, so each stored visit day is
 * either fully inside or fully outside the window. A rolling 1d cutoff does
 * not, and the open UTC day alone cannot stand in for the last 24 hours.
 */
export function pagePerformanceVisitsNeedApportionment(
  windowStart: Date | null,
): boolean {
  return windowStart != null && !isUtcDayStart(windowStart);
}

function utcDayStartMs(day: string): number | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(day.trim());
  if (!match) return null;
  return Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}

/**
 * Fraction of a UTC daily visit bucket that falls inside [windowStart, now].
 *
 * Closed days are treated as uniform across the calendar day. The open UTC
 * day (the one that contains `now`) counts in full when the window already
 * covers every elapsed moment of that day — a rolling 24h window always
 * does. Using only that open bucket renders an empty 1d range until the
 * day closes.
 */
export function dailyVisitWindowWeight(
  day: string,
  windowStart: Date,
  now: Date,
): number {
  const dayStartMs = utcDayStartMs(day);
  if (dayStartMs == null) return 0;
  const dayEndMs = dayStartMs + MS_PER_DAY;
  const startMs = windowStart.getTime();
  const nowMs = now.getTime();
  if (!(nowMs > dayStartMs && startMs < dayEndMs)) return 0;

  const open = nowMs < dayEndMs;
  if (open) {
    const elapsed = nowMs - dayStartMs;
    if (elapsed <= 0) return 0;
    const overlap = nowMs - Math.max(dayStartMs, startMs);
    return Math.min(1, Math.max(0, overlap / elapsed));
  }

  const overlapStart = Math.max(dayStartMs, startMs);
  const overlapEnd = Math.min(dayEndMs, nowMs);
  if (overlapEnd <= overlapStart) return 0;
  return (overlapEnd - overlapStart) / MS_PER_DAY;
}

/**
 * Turn UTC daily visit rows into path totals for a window that may start
 * mid-day. Fully covered days count in full. The boundary day is scaled.
 * The still-open day counts in full (its rows are written as the day happens).
 */
export function apportionDailyPageVisits(
  rows: ReadonlyArray<{ path: string; day: string; views: number }>,
  windowStart: Date,
  now: Date,
): { path: string; visits: number }[] {
  const totals = new Map<string, number>();
  for (const row of rows) {
    const views = Number(row.views) || 0;
    if (views <= 0) continue;
    const path = normalizePublicPath(row.path);
    if (!path) continue;
    const weight = dailyVisitWindowWeight(row.day, windowStart, now);
    if (weight <= 0) continue;
    totals.set(path, (totals.get(path) ?? 0) + views * weight);
  }
  const out: { path: string; visits: number }[] = [];
  for (const [path, raw] of totals) {
    const visits = Math.round(raw);
    if (visits <= 0) continue;
    out.push({ path, visits });
  }
  return out;
}

export interface SitePageInventoryItem {
  /** Stable id for React keys / test ids (not a DB primary key for static rows). */
  id: string;
  title: string;
  /** Path beginning with `/` (no origin). */
  path: string;
  visibility: SitePageVisibility;
  kind: SitePageKind;
  /** Optional operational note (e.g. language, strategy, inactive). */
  detail?: string | null;
  /** Campaign / project live flag when known; omitted for pure static pages. */
  isLive?: boolean | null;
  visits?: number;
  signups?: number;
  inquiries?: number;
}

export interface SitePageMetrics {
  path: string;
  visits: number;
  signups: number;
  inquiries: number;
}

/**
 * Portal / API paths we do not count as public page views.
 * Match exact (`/agent`) or prefix-with-slash (`/agent/…`) so `/agents/apply`
 * and `/sponsors/apply` stay public. `/principals` is the live principal
 * portal (plural); `/principal` covers leftover singular links.
 */
const PRIVATE_PATH_ROOTS = [
  "/admin",
  "/dashboard",
  "/agent",
  "/vendor",
  "/sponsor",
  "/principal",
  "/principals",
  "/api",
  "/deal-room",
  "/documents/request",
  "/pitch-share",
  "/unsubscribe",
] as const;

/**
 * Capability URLs. Analytics may record the root only — never the token
 * segment or the full href.
 */
const REDACTED_ANALYTICS_ROOTS = ["/pitch-share"] as const;

export function redactedAnalyticsLocation(
  pathname: string,
  href: string,
): { path: string; url: string } {
  const normalized = normalizePublicPath(pathname);
  const root = normalized
    ? REDACTED_ANALYTICS_ROOTS.find(
        (candidate) =>
          normalized === candidate || normalized.startsWith(`${candidate}/`),
      )
    : undefined;
  if (!root) return { path: pathname, url: href };
  let origin = "";
  try {
    origin = new URL(href).origin;
  } catch {
    origin = "";
  }
  return { path: root, url: origin ? `${origin}${root}` : root };
}

/**
 * Collapse a stored landing_page / URL / pathname to a comparable path.
 * Strips origin, query, hash, and trailing slashes (except `/`).
 */
export function normalizePublicPath(
  raw: string | null | undefined,
): string | null {
  if (!raw) return null;
  let s = raw.trim();
  if (!s) return null;
  try {
    if (/^https?:\/\//i.test(s)) {
      s = new URL(s).pathname;
    }
  } catch {
    /* keep s */
  }
  const q = s.indexOf("?");
  if (q >= 0) s = s.slice(0, q);
  const h = s.indexOf("#");
  if (h >= 0) s = s.slice(0, h);
  if (!s.startsWith("/")) s = `/${s}`;
  s = s.replace(/\/{2,}/g, "/");
  if (s.length > 1) s = s.replace(/\/+$/, "");
  if (s.length > 200) s = s.slice(0, 200);
  return s || null;
}

export function isPublicPageViewPath(path: string): boolean {
  const normalized = normalizePublicPath(path);
  if (!normalized) return false;
  return !PRIVATE_PATH_ROOTS.some(
    (root) => normalized === root || normalized.startsWith(`${root}/`),
  );
}

/** Hardcoded public marketing surfaces known to App.tsx. */
export const STATIC_SITE_PAGES: readonly SitePageInventoryItem[] = [
  // —— Listed (nav and/or sitemap) ——
  { id: "static:/", title: "Home", path: "/", visibility: "listed", kind: "static" },
  {
    id: "static:/projects",
    title: "Projects",
    path: "/projects",
    visibility: "listed",
    kind: "static",
  },
  {
    id: "static:/knowledge-base",
    title: "Knowledge Base",
    path: "/knowledge-base",
    visibility: "listed",
    kind: "static",
  },
  {
    id: "static:/about",
    title: "About",
    path: "/about",
    visibility: "listed",
    kind: "static",
  },
  {
    id: "static:/schedule-call",
    title: "Schedule a Consultation",
    path: "/schedule-call",
    visibility: "listed",
    kind: "static",
  },
  {
    id: "static:/eb5-guide",
    title: "EB-5 Quick Guide",
    path: "/eb5-guide",
    visibility: "listed",
    kind: "static",
  },
  {
    id: "static:/funds-assessment",
    title: "Funds Assessment",
    path: "/funds-assessment",
    visibility: "listed",
    kind: "static",
  },
  {
    id: "static:/direct-eb5",
    title: "Direct vs Regional Center",
    path: "/direct-eb5",
    visibility: "listed",
    kind: "static",
  },
  {
    id: "static:/gold-card",
    title: "Gold Card vs EB-5",
    path: "/gold-card",
    visibility: "listed",
    kind: "static",
  },
  {
    id: "static:/compare",
    title: "Comparison Tool",
    path: "/compare",
    visibility: "listed",
    kind: "static",
  },
  {
    id: "static:/source-of-funds",
    title: "Source of Funds",
    path: "/source-of-funds",
    visibility: "listed",
    kind: "static",
  },
  {
    id: "static:/rc-reviews",
    title: "RC Reviews",
    path: "/rc-reviews",
    visibility: "listed",
    kind: "static",
  },
  {
    id: "static:/timeline-calculator",
    title: "Timeline Calculator",
    path: "/timeline-calculator",
    visibility: "listed",
    kind: "static",
  },
  {
    id: "static:/investor-verification",
    title: "Investor Verification",
    path: "/investor-verification",
    visibility: "listed",
    kind: "static",
  },
  {
    id: "static:/agents/apply",
    title: "Referral Agent Apply",
    path: "/agents/apply",
    visibility: "listed",
    kind: "static",
  },
  {
    id: "static:/agents/lead-referral",
    title: "EB-5 Referral Partner Program",
    path: "/agents/lead-referral",
    visibility: "listed",
    kind: "static",
  },
  {
    id: "static:/sponsors/apply",
    title: "Sponsor Apply",
    path: "/sponsors/apply",
    visibility: "listed",
    kind: "static",
  },
  {
    id: "static:/news",
    title: "News & Analysis",
    path: "/news",
    visibility: "listed",
    kind: "static",
  },
  {
    id: "static:/media",
    title: "Media",
    path: "/media",
    visibility: "listed",
    kind: "static",
  },
  {
    id: "static:/visa-bulletin",
    title: "Visa Bulletin",
    path: "/visa-bulletin",
    visibility: "listed",
    kind: "static",
  },
  {
    id: "static:/countries",
    title: "Countries",
    path: "/countries",
    visibility: "listed",
    kind: "static",
  },
  {
    id: "static:/legislation",
    title: "Legislation",
    path: "/legislation",
    visibility: "listed",
    kind: "static",
  },
  {
    id: "static:/regional-centers",
    title: "Regional Centers",
    path: "/regional-centers",
    visibility: "listed",
    kind: "static",
  },
  {
    id: "static:/forum",
    title: "Forum",
    path: "/forum",
    visibility: "listed",
    kind: "static",
  },
  {
    id: "static:/login",
    title: "Sign in",
    path: "/login",
    visibility: "listed",
    kind: "static",
  },
  {
    id: "static:/register",
    title: "Register",
    path: "/register",
    visibility: "listed",
    kind: "static",
  },
  {
    id: "static:/terms",
    title: "Terms of Service",
    path: "/terms",
    visibility: "listed",
    kind: "static",
  },
  {
    id: "static:/privacy",
    title: "Privacy Policy",
    path: "/privacy",
    visibility: "listed",
    kind: "static",
  },
  {
    id: "static:/sitemap",
    title: "Sitemap",
    path: "/sitemap",
    visibility: "listed",
    kind: "static",
  },

  // —— Unlisted marketing (live URL, not in nav/sitemap; often noindex) ——
  {
    id: "static:/energy-briefing",
    title: "Energy EB-5 Briefing",
    path: "/energy-briefing",
    visibility: "unlisted",
    kind: "static",
    detail: "Screen-share briefing · noindex",
  },
  {
    id: "static:/partner-briefing",
    title: "How We Work with Partners",
    path: "/partner-briefing",
    visibility: "unlisted",
    kind: "static",
    detail: "Screen-share partner walkthrough · noindex",
  },
  {
    id: "static:/overview",
    title: "EB-5 Overview Walkthrough",
    path: "/overview",
    visibility: "unlisted",
    kind: "static",
    detail: "Screen-share walkthrough · noindex",
  },
  {
    id: "static:/pricing",
    title: "EB-5 Pricing",
    path: "/pricing",
    visibility: "unlisted",
    kind: "static",
    detail: "Investment capital vs fee ranges · noindex",
  },
  {
    id: "static:/divisions/energy",
    title: "Energy Division",
    path: "/divisions/energy",
    visibility: "unlisted",
    kind: "division",
    detail: "Not in primary nav or XML sitemap",
  },
  {
    id: "static:/divisions/real-estate",
    title: "Real Estate Division",
    path: "/divisions/real-estate",
    visibility: "unlisted",
    kind: "division",
    detail: "Not in primary nav or XML sitemap",
  },
] as const;

export interface CampaignPageRow {
  id: string;
  slug: string;
  language: string;
  languageCode: string;
  heroTitle: string | null;
  strategy: string | null;
  isActive: boolean | null;
}

export interface LinkHubRow {
  id: string;
  slug: string;
  title: string;
  archivedAt?: string | Date | null;
}

export interface ProjectPageRow {
  id: string;
  slug: string | null;
  title: string;
  status: string | null;
}

export interface SeoPageRow {
  slug: string;
  title: string;
}

export function campaignPageToInventoryItem(
  page: CampaignPageRow,
): SitePageInventoryItem {
  const title =
    page.heroTitle?.trim() ||
    `${page.language} campaign` ||
    `Campaign /${page.slug}`;
  const strategy = page.strategy ? ` · ${page.strategy}` : "";
  return {
    id: `campaign:${page.id}`,
    title,
    path: `/invest/${page.slug}`,
    visibility: "unlisted",
    kind: "campaign",
    detail: `${page.language} (${page.languageCode})${strategy}`,
    isLive: page.isActive ?? false,
  };
}

export function linkHubToInventoryItem(hub: LinkHubRow): SitePageInventoryItem {
  const archived = !!hub.archivedAt;
  return {
    id: `hub:${hub.id}`,
    title: hub.title,
    path: `/h/${hub.slug}`,
    visibility: "unlisted",
    kind: "link_hub",
    detail: archived ? "Archived link hub" : "Link hub",
    isLive: !archived,
  };
}

export function projectToInventoryItem(
  project: ProjectPageRow,
): SitePageInventoryItem | null {
  if (!project.slug) return null;
  return {
    id: `project:${project.id}`,
    title: project.title,
    path: `/projects/${project.slug}`,
    visibility: "listed",
    kind: "project",
    detail: project.status ? `Project · ${project.status}` : "Project",
    isLive: project.status === "active",
  };
}

export function seoPageToInventoryItem(page: SeoPageRow): SitePageInventoryItem | null {
  const path = normalizePublicPath(page.slug);
  if (!path) return null;
  return {
    id: `seo:${path}`,
    title: page.title,
    path,
    visibility: "listed",
    kind: "seo",
  };
}

export function buildSitePagesInventory(input: {
  campaigns?: CampaignPageRow[];
  hubs?: LinkHubRow[];
  projects?: ProjectPageRow[];
  seo?: SeoPageRow[];
}): SitePageInventoryItem[] {
  const items: SitePageInventoryItem[] = [...STATIC_SITE_PAGES];
  const seen = new Set(items.map((p) => p.path));

  const pushUnique = (row: SitePageInventoryItem | null) => {
    if (!row || seen.has(row.path)) return;
    seen.add(row.path);
    items.push(row);
  };

  for (const page of input.campaigns ?? []) {
    pushUnique(campaignPageToInventoryItem(page));
  }
  for (const hub of input.hubs ?? []) {
    pushUnique(linkHubToInventoryItem(hub));
  }
  for (const project of input.projects ?? []) {
    pushUnique(projectToInventoryItem(project));
  }
  for (const page of input.seo ?? []) {
    pushUnique(seoPageToInventoryItem(page));
  }

  return sortSitePagesInventory(items);
}

/** Overlay first-party metrics; unknown traffic paths become extra rows. */
export function attachPageMetrics(
  pages: SitePageInventoryItem[],
  metrics: SitePageMetrics[],
): SitePageInventoryItem[] {
  const byPath = new Map<string, SitePageInventoryItem>();
  for (const page of pages) {
    const path = normalizePublicPath(page.path) ?? page.path;
    byPath.set(path, {
      ...page,
      path,
      visits: 0,
      signups: 0,
      inquiries: 0,
    });
  }

  const extras: SitePageInventoryItem[] = [];
  for (const metric of metrics) {
    const path = normalizePublicPath(metric.path);
    if (!path) continue;
    const existing = byPath.get(path);
    if (existing) {
      existing.visits = (existing.visits ?? 0) + metric.visits;
      existing.signups = (existing.signups ?? 0) + metric.signups;
      existing.inquiries = (existing.inquiries ?? 0) + metric.inquiries;
      continue;
    }
    if (metric.visits === 0 && metric.signups === 0 && metric.inquiries === 0) {
      continue;
    }
    extras.push({
      id: `other:${path}`,
      title: path,
      path,
      visibility: "unlisted",
      kind: "other",
      visits: metric.visits,
      signups: metric.signups,
      inquiries: metric.inquiries,
    });
  }

  return sortSitePagesInventory([...byPath.values(), ...extras]);
}

export function sortSitePagesInventory(
  items: SitePageInventoryItem[],
): SitePageInventoryItem[] {
  const visibilityRank = (v: SitePageVisibility) => (v === "unlisted" ? 0 : 1);
  const kindRank: Record<SitePageKind, number> = {
    static: 0,
    division: 1,
    seo: 2,
    campaign: 3,
    link_hub: 4,
    project: 5,
    other: 6,
  };
  return [...items].sort((a, b) => {
    const vr = visibilityRank(a.visibility) - visibilityRank(b.visibility);
    if (vr !== 0) return vr;
    const kr = kindRank[a.kind] - kindRank[b.kind];
    if (kr !== 0) return kr;
    return a.path.localeCompare(b.path) || a.title.localeCompare(b.title);
  });
}
